import React, { JSX, useEffect, useState } from 'react';
import Box from '@mui/material/Box';

import { BackstoryElementProps } from 'components/BackstoryTab';
import { CandidateInfo } from 'components/ui/CandidateInfo';
import { Candidate } from 'types/types';
import { useAuth } from 'hooks/AuthContext';
import { useAppState, useSelectedCandidate } from 'hooks/GlobalContext';
import { Paper } from '@mui/material';
import { Scrollable } from 'components/Scrollable';

interface CandidatePickerProps extends BackstoryElementProps {
  onSelect?: (candidate: Candidate) => void;
}

const CandidatePicker = (props: CandidatePickerProps): JSX.Element => {
  const { onSelect } = props;
  const { apiClient } = useAuth();
  const { selectedCandidate, setSelectedCandidate } = useSelectedCandidate();
  const { setSnack } = useAppState();
  const [candidates, setCandidates] = useState<Candidate[] | null>(null);

  useEffect(() => {
    if (candidates !== null) {
      return;
    }
    const getCandidates = async (): Promise<void> => {
      try {
        const results = await apiClient.getCandidates();
        const candidates: Candidate[] = results.data;
        candidates.sort((a, b) => {
          const aIsAi = 'isAI' in a ? 1 : 0;
          const bIsAi = 'isAI' in b ? 1 : 0;
          let result = aIsAi - bIsAi;
          if (result === 0) {
            result = a.lastName.localeCompare(b.lastName);
          }
          if (result === 0) {
            result = a.firstName.localeCompare(b.firstName);
          }
          if (result === 0) {
            result = a.username.localeCompare(b.username);
          }
          return result;
        });
        setCandidates(candidates);
      } catch (err) {
        setSnack('' + err);
      }
    };

    getCandidates();
  }, [candidates, setSnack, apiClient]);

  return (
    <Scrollable
      sx={{
        display: 'flex',
        flexDirection: 'column',
        m: 0,
        p: 0,
        width: '100%',
        minHeight: 0,
        position: 'relative',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}
      >
        {candidates?.map(u => (
          <Paper
            key={`${u.username}`}
            onClick={(): void => {
              onSelect ? onSelect(u) : setSelectedCandidate(u);
            }}
            sx={{ cursor: 'pointer' }}
          >
            <CandidateInfo
              variant="small"
              sx={{
                maxWidth: '100%',
                minWidth: '320px',
                width: '320px',
                cursor: 'pointer',
                backgroundColor: selectedCandidate?.id === u.id ? '#f0f0f0' : 'inherit',
                border: '2px solid transparent',
                '&:hover': {
                  border: '2px solid orange',
                },
              }}
              candidate={u}
            />
          </Paper>
        ))}
      </Box>
    </Scrollable>
  );
};

export { CandidatePicker };
